import { Box, FormControl, Grid, InputLabel, MenuItem, Select, Stack, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery } from '@mui/material'
import { TooltipButton } from '../../miscellaneous-components/tooltip-button'
import { EmissionsInputFilter } from './emissions-input/emissions-input-filter'
import { EmissionsInputDirect } from './emissions-input/emissions-input-direct'
import { FunctionComponent, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import gaiaTheme from '../../../../root-components/theme'
import { IEmission } from '../../../../shared/services/emission/emission.model'
import { IGroupDetail } from '../../../../shared/services/group/group.model'
import { EmissionsInputCard } from './emissions-display/emissions-input-card'
import { IConversionSource } from '../../../../shared/services/conversion-source/conversion-source.model'
import { ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model'
import { ILocation } from '../../../../shared/services/location/location.model'
import LocationService from '../../../../shared/services/location/location.service'
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods'
import { isLeasedAssetGroupId } from '../../../../shared/utils/group-ids-utils'
import { FloorAreaEstimator } from './floor-area-estimator'
import { getCategoryDisplayName } from '../../../../shared/utils/category-utils'
import { getCategoryCardChipLabel, getCategoryFactorAvailability } from '../../../../shared/utils/category-picker-utils'

export const EmissionsAddManual: FunctionComponent<{
  group: IGroupDetail,
  activeCategoryId: string,
  backgroundColor: string,
  onSave: any,
  isFormSubmitting: boolean,
  handleTransactionSubmit: any,
  showCategoryFormHandler: any,
  selectedEmissionFactorSourceId: string | undefined,
  emissionFactorSourceHandler: (value: IConversionSource) => void,
  conversionSources: IConversionSource[],
  subsidiaries: ISubsidiary[],
  selectedSubsidiaryId: string,
  setSelectedSubsidiaryId: React.Dispatch<React.SetStateAction<string | undefined>>,
  emission?: IEmission | null
}> = ({
  group,
  activeCategoryId,
  backgroundColor,
  onSave,
  isFormSubmitting,
  handleTransactionSubmit,
  showCategoryFormHandler,
  emissionFactorSourceHandler,
  conversionSources,
  selectedEmissionFactorSourceId,
  subsidiaries,
  selectedSubsidiaryId,
  setSelectedSubsidiaryId,
  emission,
}) => {
    //Services
    const locationService = LocationService.getInstance()
    //Context
    const reportPeriodContext = useContext(ReportPeriodContext)
    const navigate = useNavigate()
    //State
    const [data, setData] = useState<string>('filter');
    // True while the entry method was switched to direct CO2e entry FOR the user, because the card
    // they picked has no emission factors. Picking a card that has factors switches it back.
    const [directEntryForced, setDirectEntryForced] = useState<boolean>(false);
    // Picking a card reveals the entry form below the card grid, which on most screens is off the
    // bottom of the page, so the view follows it down. Only for a click: a page that opens with a
    // category already selected (editing an entry, a deep link) is left where it is.
    const entryFormRef = useRef<HTMLDivElement | null>(null);
    const scrollToFormOnSelect = useRef<boolean>(false);
    const [entryMode, setEntryMode] = useState<'standard' | 'floorArea'>('standard');
    const [locations, setLocations] = useState<ILocation[]>([]);
    const [selectedLocationId, setSelectedLocationId] = useState<string>('');
    //Layout
    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

    // Floor-area estimation is only offered on the Scope 3 Leased Assets groups it books into.
    const canEstimateFloorArea = isLeasedAssetGroupId(group.id)
    const selectedReportPeriod = reportPeriodContext.allReportPeriods.find(
      period => period.id === reportPeriodContext.lastSelectedReportId
    )
    const selectedLocation = locations.find(location => location.id === selectedLocationId)

    useEffect(() => {
      if (canEstimateFloorArea) {
        locationService.getActiveLocations()
          .then(response => setLocations(response))
          .catch(err => console.log(err))
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canEstimateFloorArea])

    const dataHandler = (e: React.MouseEvent<HTMLElement>, newData: string | null) => {
      if (newData !== null) {
        setData(newData)
        setDirectEntryForced(false)
      }
    }

    const activeCategoryAvailability = getCategoryFactorAvailability(group.categories.find(category => category.id === activeCategoryId))

    const categoryCardHandler = (categoryId: string) => {
      const availability = getCategoryFactorAvailability(group.categories.find(category => category.id === categoryId))
      if (availability === 'none') {
        setData('direct')
        setDirectEntryForced(true)
      } else if (directEntryForced) {
        setData('filter')
        setDirectEntryForced(false)
      }
      scrollToFormOnSelect.current = true
      showCategoryFormHandler(categoryId)
    }

    useEffect(() => {
      if (!scrollToFormOnSelect.current || !activeCategoryId) return
      scrollToFormOnSelect.current = false
      // Next frame, so the form has rendered and has a position to scroll to.
      const frame = requestAnimationFrame(() => {
        entryFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
      return () => cancelAnimationFrame(frame)
    }, [activeCategoryId])

    const entryModeHandler = (e: React.MouseEvent<HTMLElement>, newMode: 'standard' | 'floorArea' | null) => {
      if (newMode !== null) {
        setEntryMode(newMode)
      }
    }

    return (
      <>
        <Grid container display='flex' flexDirection='row' justifyContent='space-between' alignContent='center' sx={{ pb: 3 }} >
          <Grid item xl={9} lg={9} md={9} sm={12} xs={12}><Typography variant='h6'> {`Add ${group.title} Emissions`}</Typography>
          </Grid>
        </Grid>
        {canEstimateFloorArea &&
          <Stack display='flex' flexDirection='row' alignItems='center' sx={{ width: '100%', mb: 4 }}>
            <ToggleButtonGroup value={entryMode} exclusive onChange={entryModeHandler} orientation={isMobileMode ? 'vertical' : 'horizontal'}>
              <ToggleButton value='standard' sx={{ pt: 1, pb: 0.75, width: !isMobileMode ? '225px' : '100%' }}><Typography variant='body1' sx={{ color: 'text.primary', textTransform: 'capitalize' }}>Add Emissions</Typography></ToggleButton>
              <ToggleButton value='floorArea' sx={{ pt: 1, pb: 0.75, width: !isMobileMode ? '225px' : '100%' }}><Typography variant='body1' sx={{ color: 'text.primary', textTransform: 'capitalize' }}>Estimate from Floor Area</Typography></ToggleButton>
            </ToggleButtonGroup>
            {!isMobileMode &&
              <TooltipButton
                noPadding={true}
                text="Estimate a leased site's utility emissions from its floor area when you don't hold the utility bills."
                placement='right' />}
          </Stack>}
        {entryMode === 'floorArea' ?
          <Box sx={{ minHeight: '400px', marginBottom: 8 }}>
            <FormControl fullWidth variant='outlined' sx={{ mb: 3 }}>
              <InputLabel htmlFor='floorAreaLocationId'>Location</InputLabel>
              <Select
                id='floorAreaLocationId'
                label='Location'
                value={selectedLocationId}
                onChange={(event) => setSelectedLocationId(event.target.value)}
              >
                {[...locations].sort((a, b) => a.friendlyName.localeCompare(b.friendlyName)).map((location) => (
                  <MenuItem key={location.id} value={location.id}>{location.friendlyName}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedLocation && selectedReportPeriod ?
              <FloorAreaEstimator
                key={selectedLocation.id}
                location={selectedLocation}
                reportPeriodId={selectedReportPeriod.id}
                startDate={moment(selectedReportPeriod.startDate).format('YYYY-MM-DD')}
                endDate={moment(selectedReportPeriod.endDate).format('YYYY-MM-DD')}
                groupId={group.id}
                onSuccess={(result) => {
                  setSelectedLocationId('')
                  // Land in the created emission (matching the standard add flow). Its scope may be
                  // 1/2/3 depending on the location's control status, so it may not belong to the
                  // group being viewed; navigating to the emission avoids showing an empty table.
                  if (result?.emissionId) {
                    navigate(`/emissions/overview/${result.emissionId}`)
                  } else {
                    onSave()
                  }
                }}
              /> :
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                {selectedReportPeriod ? 'Select a location to estimate its utilities from floor area.' : 'Select a report period before estimating utilities.'}
              </Typography>}
          </Box> :
          <>
            <Typography variant='subtitle1' sx={{ mb: 0.5 }}><b>Choose a category</b></Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
              The category decides which emission factors you can pick from. You can always enter a CO<sub>2</sub>e figure directly.
            </Typography>
            <Grid container spacing={4}>
              {group.categories.map((category, index) => {
                const chipLabel = getCategoryCardChipLabel(getCategoryFactorAvailability(category))
                return (
                <Grid key={index} item lg={4} xs={6} >
                  <Box onClick={() => { categoryCardHandler(category.id) }} sx={{ cursor: 'pointer', height: '100%', }}>
                    <EmissionsInputCard
                      title={getCategoryDisplayName(category)}
                      description={category.description}
                      icon={category.icon}
                      backgroundColor={backgroundColor}
                      selected={activeCategoryId === category.id}
                      showChip={!!chipLabel}
                      chipLabel={chipLabel}
                    />
                  </Box>
                </Grid>
                )
              })}
            </Grid>
            {activeCategoryId &&
              <>
                {/* scrollMarginTop keeps the method toggle clear of the fixed top bar after scrolling. */}
                <Stack ref={entryFormRef} display='flex' flexDirection='row' alignItems='center' sx={{ width: '100%', mt: 4, scrollMarginTop: '96px' }}>
                  <ToggleButtonGroup value={data} exclusive onChange={dataHandler} orientation={isMobileMode ? "vertical" : 'horizontal'}>
                    <ToggleButton value='filter' disabled={activeCategoryAvailability === 'none'} sx={{ pt: 1, pb: 0.75, width: !isMobileMode ? '225px' : '100%', }}><Typography variant='body1' sx={{ color: 'text.primary', textTransform: 'capitalize' }}>Calculate Emissions</Typography></ToggleButton>
                    <ToggleButton value='direct' sx={{ pt: 1, pb: 0.75, width: !isMobileMode ? '225px' : '100%', }}><Typography variant='body1' sx={{ color: 'text.primary', textTransform: 'capitalize' }}>Enter Emissions in CO<sub>2</sub>e</Typography></ToggleButton>
                  </ToggleButtonGroup>
                  {!isMobileMode &&
                    <TooltipButton
                      noPadding={true}
                      text='You can either calculate emissions by entering additional information for our system to compute the CO2e amount, or if you already know the CO2e, simply enter it directly.'
                      placement='right' />}
                </Stack>
                {activeCategoryAvailability === 'none' &&
                  <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1 }}>
                    There are no emission factors for this category in {group.title}, so enter the CO<sub>2</sub>e figure directly.
                  </Typography>}
                <Typography variant='h6' sx={{ mt: 2 }}> {`Add Emissions`}</Typography>
                {(data === 'filter') &&
                  <Box sx={{ minHeight: '400px', marginBottom: 8 }}>
                    <EmissionsInputFilter
                      setSelectedSubsidiaryId={setSelectedSubsidiaryId}
                      selectedSubsidiaryId={selectedSubsidiaryId}
                      isSubmitting={isFormSubmitting}
                      conversionSources={conversionSources}
                      emissionFactorSourceHandler={emissionFactorSourceHandler}
                      subsidiaries={subsidiaries}
                      selectedEmissionFactorSourceId={selectedEmissionFactorSourceId}
                      categoryId={activeCategoryId}
                      allowMarketBasedEmissionInput={group.categories.find(category => category.id === activeCategoryId)?.allowMarketBasedMultiplier}
                      onSubmit={(transaction: any) => handleTransactionSubmit(transaction)}
                      groupId={group.id}
                      emission={emission}
                      cancelHandler={() => showCategoryFormHandler('')}
                    />
                  </Box>}
                {data === 'direct' &&
                  <EmissionsInputDirect
                    selectedSubsidiaryId={selectedSubsidiaryId}
                    subsidiaries={subsidiaries}
                    onSave={onSave}
                    categoryId={activeCategoryId}
                    groupId={group.id}
                    emission={emission}
                    cancelHandler={() => showCategoryFormHandler('')}
                  />
                }
              </>}
          </>}
      </>
    )
  }
